import OpenAI from 'openai';
import { Anthropic } from '@anthropic-ai/sdk';
import { GoogleGenerativeAI } from '@google/generative-ai';

class AIClient {
  constructor() {
    this.initializeClients();
  }

  initializeClients() {
    // Check for required API keys
    const anthropicKey = process.env.REACT_APP_ANTHROPIC_API_KEY;
    const openaiKey = process.env.REACT_APP_OPENAI_API_KEY;
    const geminiKey = process.env.REACT_APP_GEMINI_API_KEY;

    if (!anthropicKey && !openaiKey && !geminiKey) {
      console.error('No AI provider API keys found in environment variables');
    }

    // Initialize clients only if API keys are available
    if (anthropicKey) {
      try {
        this.anthropic = new Anthropic({
          apiKey: anthropicKey,
        });
      } catch (error) {
        console.error('Failed to initialize Anthropic client:', error);
      }
    }

    if (openaiKey) {
      try {
        this.openai = new OpenAI({
          apiKey: openaiKey,
        });
      } catch (error) {
        console.error('Failed to initialize OpenAI client:', error);
      }
    }

    if (geminiKey) {
      try {
        this.gemini = new GoogleGenerativeAI(geminiKey);
      } catch (error) {
        console.error('Failed to initialize Gemini client:', error);
      }
    }
  }

  async generateWithAnthropic(prompt, options = {}) {
    if (!this.anthropic) {
      throw new Error('Anthropic client not initialized. Please check your API key.');
    }

    try {
      const response = await this.anthropic.messages.create({
        model: options.model || process.env.REACT_APP_ANTHROPIC_MODEL || 'claude-3-opus-20240229',
        max_tokens: options.maxTokens || 4096,
        messages: [{ role: 'user', content: prompt }],
        temperature: options.temperature || 0.7,
      });
      return response.content[0].text;
    } catch (error) {
      console.error('Anthropic SDK Error:', error);
      throw new Error(`Failed to generate content with Anthropic: ${error.message}`);
    }
  }

  async generateWithOpenAI(prompt, options = {}) {
    if (!this.openai) {
      throw new Error('OpenAI client not initialized. Please check your API key.');
    }

    try {
      const response = await this.openai.chat.completions.create({
        model: options.model || process.env.REACT_APP_OPENAI_MODEL || 'gpt-4-turbo-preview',
        messages: [{ role: 'user', content: prompt }],
        temperature: options.temperature || 0.7,
        max_tokens: options.maxTokens || 4096,
      });
      return response.choices[0].message.content;
    } catch (error) {
      console.error('OpenAI SDK Error:', error);
      throw new Error(`Failed to generate content with OpenAI: ${error.message}`);
    }
  }

  async generateWithGemini(prompt, options = {}) {
    if (!this.gemini) {
      throw new Error('Gemini client not initialized. Please check your API key.');
    }

    try {
      const model = this.gemini.getGenerativeModel({ 
        model: options.model || process.env.REACT_APP_GEMINI_MODEL || 'gemini-pro'
      });
      
      const result = await model.generateContent(prompt);
      return result.response.text();
    } catch (error) {
      console.error('Gemini SDK Error:', error);
      throw new Error(`Failed to generate content with Gemini: ${error.message}`);
    }
  }

  async checkAvailability() {
    try {
      // Try each available provider in order of preference
      if (this.anthropic) {
        await this.generateWithAnthropic('test', { maxTokens: 10 });
        return true;
      }
      if (this.openai) {
        await this.generateWithOpenAI('test', { maxTokens: 10 });
        return true;
      }
      if (this.gemini) {
        await this.generateWithGemini('test');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Service availability check failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const aiClient = new AIClient(); 